import { useState, useEffect, useRef } from "react";
import { FaChevronDown } from "react-icons/fa";

const MultiSelectInput = ({
    className,
    label,
    placeholder = "Select an option",
    options = [],
    name,
    value,
    onChange = () => { }, // avoid crash
    mandatory,
    required = false,
    error = "",
    border,
    multiple = false, // 👈 multiple add support
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [localOptions, setLocalOptions] = useState(options);
    const containerRef = useRef(null);

    // keep localOptions in sync if props.options changes (optional)
    useEffect(() => {
        setLocalOptions(options);
    }, [options]);

    const filteredOptions = localOptions.filter((option) =>
        option.label.toLowerCase().includes(search.toLowerCase())
    );

    // Close when clicking outside
    useEffect(() => {
        const handleDocClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("click", handleDocClick);
        return () => document.removeEventListener("click", handleDocClick);
    }, []);

    const handleSelect = (selectedValue) => {
        if (multiple) {
            let newValue = Array.isArray(value) ? [...value] : [];
            if (newValue.includes(selectedValue)) {
                newValue = newValue.filter((v) => v !== selectedValue);
            } else {
                newValue.push(selectedValue);
            }
            onChange({ target: { name, value: newValue } });
        } else {
            onChange({ target: { name, value: selectedValue } });
        }

        // close dropdown immediately (for both multiple & single)
        setIsOpen(false);
        setSearch("");
    };

    const handleAddOption = () => {
        if (!search.trim()) return;

        const newOption = { label: search, value: search.toLowerCase() };

        if (!localOptions.some((opt) => opt.value === newOption.value)) {
            setLocalOptions((prev) => [...prev, newOption]);
        }

        if (multiple) {
            let newValue = Array.isArray(value) ? [...value] : [];
            if (!newValue.includes(newOption.value)) {
                newValue.push(newOption.value);
            }
            onChange({ target: { name, value: newValue } });
        } else {
            onChange({ target: { name, value: newOption.value } });
        }

        // close dropdown immediately after add
        setIsOpen(false);
        setSearch("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddOption();
        }
    };

    return (
        <div className="mb-4 relative" ref={containerRef}>
            {label && (
                <label className="block mb-1 text-gray-700 font-medium text-[14px]">
                    {label}{" "}
                    <sup className="text-red-500 text-sm">{mandatory ? "*" : ""}</sup>
                </label>
            )}
            <div
                className={`${className} ${border ? "border" : "shadow"} rounded-lg py-2 px-3 flex flex-wrap gap-2 items-center cursor-pointer bg-white min-h-[45px]`}
                onClick={() => setIsOpen((s) => !s)}
            >
                {multiple ? (
                    value?.length > 0 ? (
                        value.map((v) => {
                            const option = localOptions.find((o) => o.value === v);
                            return (
                                <div
                                    key={v}
                                    className="flex items-center gap-2 bg-[#fbf4f7] text-gray-800 px-4 rounded-full shadow-md text-sm font-medium hover:shadow-lg transition-all duration-200"
                                >
                                    <p className="truncate max-w-[120px] text-secondary-400">{option?.label || v}</p>
                                    <button
                                        type="button"
                                        className="flex items-center font-bold justify-center rounded-full text-lg shadow-sm transition-all duration-200 transform hover:scale-110"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            let newValue = value.filter((val) => val !== v);
                                            onChange({ target: { name, value: newValue } });
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            );
                        })
                    ) : (
                        <span className="text-gray-400 text-sm">{placeholder}</span>
                    )
                ) : (
                    <span className={`${value ? "text-black" : "text-gray-400"} text-sm`}>
                        {localOptions.find((o) => o.value === value)?.label || placeholder}
                    </span>
                )}
                <FaChevronDown className="ml-auto" />
            </div>

            {isOpen && (
                <div>
                    <div className="overflow-x-hidden absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-auto shadow-lg">
                        <input
                            type="text"
                            placeholder="Search or add..."
                            className={`px-3 py-3 border-b outline-none text-sm placeholder-gray-400 placeholder:text-sm w-full`}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                            required={required}
                        />
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <div
                                    key={option.value}
                                    onClick={() => handleSelect(option.value)}
                                    className={`px-4 py-2 hover:bg-gradient-to-r from-[#ECF1FF] to-[#EED8FF] cursor-pointer text-sm ${multiple && value?.includes(option.value) ? "bg-[#EED8FF]" : ""}`}
                                >
                                    {option.label}
                                </div>
                            ))
                        ) : (
                            search && (
                                <div
                                    className="px-4 py-2 text-blue-600 cursor-pointer text-sm hover:underline"
                                    onClick={handleAddOption}
                                >
                                    + Add "{search}"
                                </div>
                            )
                        )}
                    </div>
                </div>
            )}

            {error && (
                <div className="w-full bg-red-100 rounded-md h-auto">
                    <p className="text-red-500 text-sm mt-1 ml-3">{error}</p>
                </div>
            )}
        </div>
    );
};

export default MultiSelectInput;



